// imports
import './styles/graphic.scss';
import * as d3 from 'd3';

let svg;
let xAxis;
let xAxisLabel;
let yAxis;
let zeroLine;
let moreAffordable;
let moreAffordableNote;
let lessAffordable;
let lessAffordableNote;
let affordableLabels;
let affordabilityLabel;
let margin;
let width;
let height;


let shadingRect;

let prevValProp;
let prevWidth;
let chartGroup;
let barsGroup;
let containerNode;
let jsonDataGlobal;
let labelsGroup;

// Config
const defaultTransitionTime = 300;
const heightTotal = 500;

const X_MAX = 200;
const Y_MAX = 120;
const BIN_SIZE = 10;

// Utility
function getMargins() {
  return window.innerWidth < 600
    ? {
      top: 24, right: 20, bottom: 40, left: 30, height: 350,
    } // Mobile
    : {
      top: 24, right: 40, bottom: 40, left: 30, height: 300,
    }; // Desktop
}

function setDimensions(container) {
  margin = getMargins();
  width = container.node().getBoundingClientRect().width - margin.left - margin.right;
  height = margin.height - margin.top - margin.bottom;
}

// Setup SVG + Axes
function setupChart(container, config) {
  setDimensions(container);
  container.html('');
  svg = container.append('svg')
    .attr('width', width + margin.left + margin.right)
    .attr('height', height + margin.top + margin.bottom);

  chartGroup = svg.append('g')
    .attr('class', 'chart')
    .attr('transform', `translate(${margin.left},${margin.top})`);

  chartGroup.selectAll('.shading').remove();
  shadingRect = chartGroup.append('rect')
    .attr('class', 'shading')
    .attr('width', 0)
    .attr('height', 0)
    .attr('fill', '#f0f0f0')
    .lower(); // Ensure this is at the bottom

  xAxis = chartGroup.append('g')
    .attr('class', 'x-axis')
    .attr('transform', `translate(0,${height})`);

  yAxis = chartGroup.append('g')
    .attr('class', 'y-axis');

  barsGroup = chartGroup.append('g');
  labelsGroup = chartGroup.append('g').attr('class', 'bar-labels');


  zeroLine = chartGroup.append('line')
    .attr('class', 'zero-line')
    .attr('y1', -5)
    .attr('y2', height + 0);

  affordableLabels = chartGroup.append('g');

  moreAffordable = affordableLabels
    .append('g')
    .attr('transform', 'translate(10,0)');


  const moreAffordableTxt = moreAffordable
    .append('text')
    .attr('class', 'more-affordable')
    .attr('text-anchor', 'start')
    .attr('dx', '0')
    .attr('dy', '0')
    .text('More affordable');

  moreAffordableNote = moreAffordable
    .append('text')
    .attr('class', 'more-affordable affordable-note')
    .attr('text-anchor', 'start')
    .attr('dx', '0')
    .attr('dy', '14');

  const moreAffordableArrow = moreAffordable.append('g')
    .attr('transform', 'translate(112,-12) rotate(90) scale(.8)')
    .attr('class', 'arrow')
    .append('polygon')
    .attr('points', '19.68 9.84 9.84 0 0 9.84 4.44 9.84 4.44 23.39 15.24 23.39 15.24 9.84 19.68 9.84')
    .attr('fill', '#53A796')
    .attr('fill-opacity', '1');

  lessAffordable = affordableLabels
    .append('g')
    .attr('transform', 'translate(-10,0)');

  const lessAffordableTxt = lessAffordable
    .append('text')
    .attr('class', 'less-affordable')
    .attr('text-anchor', 'end')
    .attr('dx', '0')
    .attr('dy', '0')
    .text('More expensive');

  lessAffordableNote = lessAffordable
    .append('text')
    .attr('class', 'less-affordable affordable-note')
    .attr('text-anchor', 'end')
    .attr('dx', '0')
    .attr('dy', '14');

  const lessAffordableArrow = lessAffordable.append('g')
    .attr('transform', 'translate(-112,4) rotate(-90) scale(.8)')
    .attr('class', 'arrow')
    .append('polygon')
    .attr('points', '19.68 9.84 9.84 0 0 9.84 4.44 9.84 4.44 23.39 15.24 23.39 15.24 9.84 19.68 9.84')
    .attr('fill', '#F18851')
    .attr('fill-opacity', '1');

  affordabilityLabel = chartGroup
    .append('text')
    .attr('class', 'affordability-label')
    .attr('text-anchor', 'middle')
    .attr('dx', '0')
    .attr('dy', '-2')
    .attr('opacity', '0')
    .text('Affordability threshold');

  affordabilityLabel.append('tspan')
    .attr('x', 0)
    .attr('dy', 0)
    .attr('text-anchor', 'middle')
    .text('Affordability threshold');

  xAxisLabel = chartGroup.append('text')
    .attr('class', 'axis-label')
    .attr('transform', `translate(${width / 2},${height + 34})`)
    .attr('text-anchor', 'middle')
    .text('Affordability Index');
}

// Draw/Update Bars
function updateChart(val_prop) {
  const data = jsonDataGlobal.data.metros.map(d => d[val_prop]);


  const unaffordable_metros = data.filter(d => +d < 100);


  const x = d3.scaleLinear()
    .domain([0, X_MAX])
    .nice()
    .range([0, width]);

  const thresholds = d3.range(0, 351, BIN_SIZE);

  const bins = d3.bin()
    .domain(x.domain())
    .thresholds(thresholds)(data);


  const threshold = 100;

  const counts = data.reduce(
    (acc, num) => {
      if (num < threshold) acc.below++;
      else if (num >= threshold) acc.above++;
      else acc.equal++;
      return acc;
    },
    { below: 0, equal: 0, above: 0 },
  );


  const y = d3.scaleLinear()
    // .domain([0, d3.max(bins, d => d.length)])
    .domain([0, Y_MAX])
    .nice()
    .range([height, 0]);


  shadingRect
    .attr('width', x(100))
    .attr('height', y(0));

  zeroLine
    .attr('x1', x(100))
    .attr('x2', x(100));

  affordableLabels
    .attr('transform', `translate(${x(100)}, ${16})`);

  affordabilityLabel
    .attr('transform', `translate(${x(100)}, ${-10})`)
    .attr('opacity', '1');


  lessAffordableNote.html(`(<tspan>${counts.below}</tspan> metro areas)`);
  moreAffordableNote.html(`(<tspan>${counts.above}</tspan> metro areas)`);

  // Update axes
  xAxis.transition().duration(defaultTransitionTime)
    .call(d3.axisBottom(x));

  yAxis.transition().duration(defaultTransitionTime)
    .call(d3.axisLeft(y).tickValues([40, 80]));

  yAxis.select('.domain').remove();

  // Bind bars
  const bars = barsGroup.selectAll('.bar')
    .data(bins, d => d.x0); // use x0 as key

  // EXIT
  bars.exit()
    .transition().duration(defaultTransitionTime)
    .attr('y', y(0))
    .attr('height', 0)
    .remove();

  // UPDATE
  bars.transition().duration(defaultTransitionTime)
    .attr('class', d => `bar ${d.x0 < 100 ? 'bar-below-threshold' : 'bar-above-threshold'}`)
    .attr('x', d => x(d.x0) + 1)
    .attr('y', d => y(d.length))
    .attr('width', d => Math.max(0, x(d.x1) - x(d.x0) - 1))
    .attr('height', d => height - y(d.length));

  // ENTER
  bars.enter().append('rect')
    .attr('class', d => `bar ${d.x0 < 100 ? 'bar-below-threshold' : 'bar-above-threshold'}`)
    .attr('x', d => x(d.x0) + 1)
    .attr('y', y(0))
    .attr('width', d => Math.max(0, x(d.x1) - x(d.x0) - 1))
    .attr('height', 0)
    .transition()
    .duration(defaultTransitionTime)
    .attr('y', d => y(d.length))
    .attr('height', d => height - y(d.length));

  // LABELS
  const isMobile = window.innerWidth < 600;
  const maxCount = d3.max(bins, d => d.length);
  const labelPadding = 4;
  const fontSize = 12;
  const minInsideHeight = fontSize * 1.4;
  const labelShowDelay = defaultTransitionTime; // reveal after bars animate

  function xCenter(d) {
    return (x(d.x0) + x(d.x1)) / 2;
  }

  const labels = labelsGroup.selectAll('text.bar-label')
    .data(bins, d => d.x0);

  labels.exit().remove();

  labels
    .attr('x', d => xCenter(d))
    .attr('y', (d) => {
      const barTop = y(d.length);
      const barHeight = height - barTop;
      const inside = !isMobile && (barHeight >= minInsideHeight);
      return inside ? (barTop + labelPadding + fontSize * 0.8) : Math.max(barTop - labelPadding, 0);
    })
    .text(d => (d.length > 0 ? d3.format(',')(d.length) : ''))
    .attr('text-anchor', 'middle')
    .attr('data-x0', d => d.x0)
    .classed('inside', d => (!isMobile && ((height - y(d.length)) >= minInsideHeight)))
    .classed('is-max', d => d.length === maxCount && d.length > 0)
    .classed('is-hidden', d => !(d.length === maxCount && d.length > 0))
    .style('opacity', 0)
    .transition()
    .delay(d => ((d.length === maxCount && d.length > 0) ? labelShowDelay : 0))
    .duration(defaultTransitionTime)
    .style('opacity', d => ((d.length === maxCount && d.length > 0) ? 1 : 0));

  labels.enter()
    .append('text')
    .attr('class', 'bar-label')
    .attr('text-anchor', 'middle')
    .style('opacity', 0)
    .attr('x', d => xCenter(d))
    .attr('y', (d) => {
      const barTop = y(d.length);
      const barHeight = height - barTop;
      const inside = !isMobile && (barHeight >= minInsideHeight);
      return inside ? (barTop + labelPadding + fontSize * 0.8) : Math.max(barTop - labelPadding, 0);
    })
    .text(d => (d.length > 0 ? d3.format(',')(d.length) : ''))
    .attr('data-x0', d => d.x0)
    .classed('inside', d => (!isMobile && ((height - y(d.length)) >= minInsideHeight)))
    .classed('is-max', d => d.length === maxCount && d.length > 0)
    .classed('is-hidden', d => !(d.length === maxCount && d.length > 0))
    .transition()
    .delay(d => ((d.length === maxCount && d.length > 0) ? labelShowDelay : 0))
    .duration(defaultTransitionTime)
    .style('opacity', d => ((d.length === maxCount && d.length > 0) ? 1 : 0));

  // HOVER interactions: show label on hover, hide on leave unless it's the max
  barsGroup.selectAll('.bar')
    .on('mouseenter', (event, d) => {
      const lbl = labelsGroup.select(`text.bar-label[data-x0="${d.x0}"]`);
      lbl.classed('is-hidden', false).style('opacity', 1);
    })
    .on('mouseleave', (event, d) => {
      const isMax = d.length === maxCount && d.length > 0;
      const lbl = labelsGroup.select(`text.bar-label[data-x0="${d.x0}"]`);
      if (!isMax) {
        lbl.classed('is-hidden', true).style('opacity', 0);
      }
    });
}

// Main
export default function initHistogram(config, container, isMobile, jsonData, val_prop = 'last_score') {
  let initialWidth;
  let initialHeight;
  if (config) {
    initialWidth = config.initialWidth;
    initialHeight = config.initialHeight;
  }

  const shouldRedraw = initialWidth && initialWidth !== prevWidth;
  const shouldUpdate = val_prop !== prevValProp;

  containerNode = container;
  jsonDataGlobal = jsonData;


  if (!svg || shouldRedraw) {
    // Remove old chart
    container.selectAll('svg').remove();
    svg = null;
    xAxis = null;
    yAxis = null;
    chartGroup = null;

    setupChart(container, config);
    updateChart(val_prop);
  } else if (shouldUpdate) {
    updateChart(val_prop);
  }

  // Save state
  if (config) {
    prevWidth = initialWidth;
  }
  prevValProp = val_prop;

  if (window.pymChild) window.pymChild.sendHeight();
}
